import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { logAdminAction } from '../../utils/auditLogger';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';

export default function CreateCustomerForm({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [sendLoginEmail, setSendLoginEmail] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    status: 'Új partner',
    primaryContact: {
      email: '',
      phone: '',
      firstName: '',
      lastName: ''
    },
    address: '',
    city: '',
    zipCode: '',
    country: 'Magyarország',
    inviteCode: '',
    notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Teljes név összeállítása
      const fullName = `${formData.primaryContact.firstName} ${formData.primaryContact.lastName}`.trim();
      
      // Teljes cím összeállítása
      const fullAddress = [
        formData.address,
        formData.city,
        formData.zipCode,
        formData.country
      ].filter(Boolean).join(', ');

      // Adatok előkészítése
      const customerData = {
        name: formData.name || fullName, // Ha nincs cégnév, akkor a személy neve
        primaryContact: formData.primaryContact,
        address: formData.address,
        city: formData.city,
        zipCode: formData.zipCode,
        country: formData.country,
        fullAddress,
        status: formData.status,
        inviteCode: formData.inviteCode || null,
        notes: formData.notes || null,
        createdAt: serverTimestamp(),
        createdBy: 'admin', // Ezt később lehet dinamikusan beállítani
        updatedAt: serverTimestamp()
      };

      // Ügyfél hozzáadása az adatbázishoz
      const docRef = await addDoc(collection(db, 'customers'), customerData);

      // Audit log
      await logAdminAction('customer.create', {
        customerId: docRef.id,
        name: customerData.name
      });

      // Ha be van jelölve a jelszó-visszaállítási e-mail küldése
      if (sendLoginEmail && formData.primaryContact.email) {
        try {
          // Az új Cloud Function hívása a felhasználó létrehozásához és a jelszó-visszaállító e-mail küldéséhez
          const response = await fetch('https://us-central1-elonyklub.cloudfunctions.net/createUserAndSendResetEmail', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: formData.primaryContact.email,
              displayName: fullName,
              phone: formData.primaryContact.phone || '',
              address: fullAddress || '',
              isAdmin: false
            }),
          });

          const data = await response.json();

          if (!data.success) {
            throw new Error(data.error || 'Hiba történt a felhasználó létrehozásakor');
          }

          toast.success('Jelszó-beállító email elküldve az ügyfélnek!');
          
          // Frissítjük a customer adatokat, hogy jelezzük, hogy küldtünk e-mailt
          await addDoc(collection(db, `customers/${docRef.id}/notifications`), {
            type: 'password_reset',
            sentAt: serverTimestamp(),
            email: formData.primaryContact.email
          });
          
        } catch (authError) {
          console.error('Error sending password reset email:', authError);
          toast.error('Hiba történt a jelszó-beállító e-mail küldésekor');
        }
      }

      toast.success('Ügyfél sikeresen létrehozva!');
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error('Hiba történt az ügyfél létrehozásakor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Cégnév */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cégnév / Szervezet neve
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Cégnév vagy szervezet neve"
            />
          </div>

          {/* Státusz */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Státusz
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="Új partner">Új partner</option>
              <option value="Aktív">Aktív</option>
              <option value="Inaktív">Inaktív</option>
            </select>
          </div>
        </div>

        {/* Kapcsolattartó adatok */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium text-gray-700 mb-3">Kapcsolattartó adatai</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Vezetéknév */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vezetéknév
              </label>
              <input
                type="text"
                name="primaryContact.lastName"
                value={formData.primaryContact.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            {/* Keresztnév */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Keresztnév
              </label>
              <input
                type="text"
                name="primaryContact.firstName"
                value={formData.primaryContact.firstName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email cím
              </label>
              <input
                type="email"
                name="primaryContact.email"
                value={formData.primaryContact.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            {/* Telefonszám */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefonszám
              </label>
              <input
                type="tel"
                name="primaryContact.phone"
                value={formData.primaryContact.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Cím adatok */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium text-gray-700 mb-3">Cím adatok</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cím */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cím
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Város */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Város
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Irányítószám */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Irányítószám
              </label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Ország */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ország
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Egyéb adatok */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Meghívó kód */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meghívó kód (opcionális)
            </label>
            <input
              type="text"
              name="inviteCode"
              value={formData.inviteCode}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Egyedi meghívó kód"
            />
          </div>
        </div>

        {/* Megjegyzések */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Megjegyzések (opcionális)
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="További információk az ügyfélről..."
          />
        </div>

        {/* Jelszó-visszaállítási e-mail küldése */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="sendLoginEmail"
                type="checkbox"
                checked={sendLoginEmail}
                onChange={(e) => setSendLoginEmail(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="sendLoginEmail" className="text-sm font-medium text-gray-700">
                Jelszó-visszaállítási e-mail küldése az ügyfélnek
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Az ügyfél e-mail címére küldünk egy linket, amelyen keresztül beállíthatja jelszavát és beléphet a rendszerbe.
              </p>
            </div>
          </div>
        </div>

        {/* Gombok */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Mégse
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 flex items-center"
            disabled={loading}
          >
            {loading ? (
              <>
                <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Feldolgozás...
              </>
            ) : (
              <>
                <Icons.UserPlus className="h-4 w-4 mr-2" />
                Ügyfél létrehozása
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
} 